// Canvas-Hauptkomponente: Pan/Zoom, Tool-Modus, Selection, Drag, Undo/Redo,
// Tool Rail links, Bottom Strip unten. Slash oeffnet Method-Picker.

import type { MethodId, ShapeKind, ShapeSemantics, StickyColor } from "@ms-whiteboard/shared";
import type { Link } from "@ms-whiteboard/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type * as Y from "yjs";
import { useAuth } from "../auth/AuthContext.js";
import { type BoardDto, boardsApi } from "../board-list/api.js";
import { normalizeRename } from "../board-list/rename.js";
import { ShareModal } from "../board-share/ShareModal.js";
import { ExportModal } from "../export/ExportModal.js";
import { MethodPickerModal } from "../methods/MethodPickerModal.js";
import { insertMethodScaffold, methodLabelKeys, newCanvasId } from "../methods/insert-method.js";
import { type AwarenessUser, colorForUserId, useAwareness } from "../realtime/use-awareness.js";
import { useBoardDoc } from "../realtime/use-board-doc.js";
import { useTreeSync } from "../realtime/use-tree-sync.js";
import { BottomStrip } from "./BottomStrip.js";
import { FloatingPropertyBar } from "./FloatingPropertyBar.js";
import { LinkLayer, type PendingLink } from "./LinkLayer.js";
import { Presence } from "./Presence.js";
import { RemoteCursors } from "./RemoteCursors.js";
import { SettingsMenu } from "./SettingsMenu.js";
import { ToolRail } from "./ToolRail.js";
import { autoRouteIntoFrame, autoRouteIntoRegion, regionAcceptsType } from "./auto-route.js";
import { BoardIdContext } from "./board-context.js";
import { findChild, findParent, findSiblingNext, findSiblingPrev } from "./canvas-tree-nav.js";
import {
  clipboardHasContent,
  copySelection,
  duplicateSelection,
  pasteClipboard,
} from "./clipboard.js";
import {
  ImageUploadError,
  fitDisplaySize,
  imageFilesFromClipboard,
  imageFilesFromDrop,
  uploadImage,
} from "./image-upload.js";
import { type MarqueeRect, normalizeRect, pickObjectsInRect } from "./marquee.js";
import { applyMindmapLayout } from "./mindmap-layout.js";
import { objectBounds } from "./object-bounds.js";
import { type PortEdge, portWorldPoint } from "./object-bounds.js";
import { ObjectLayer, type ObjectLayerMode } from "./objects/ObjectLayer.js";
import { type CanvasObject, type MindmapNodeObj, isCanvasObject } from "./objects/types.js";
import { findFrameAt, findRegionAt } from "./region-routing.js";
import { type Tool, isTypingTarget, useTool } from "./tool-state.js";
import { useLinks } from "./use-links.js";
import { useSelection } from "./use-selection.js";
import { useUndoManager } from "./use-undo-manager.js";
import { useViewport } from "./use-viewport.js";
import { bringForward, bringToFront, sendBackward, sendToBack } from "./z-order.js";
import "./Canvas.css";

const GRID_BASE_PX = 32;
const PAN_DRAG_THRESHOLD_PX = 4;

interface PanState {
  startScreenX: number;
  startScreenY: number;
  startTx: number;
  startTy: number;
  moved: boolean;
}

interface MarqueeState {
  startWorld: { x: number; y: number };
  startScreenX: number;
  startScreenY: number;
  additive: boolean;
  moved: boolean;
}

export function Canvas({ boardId }: { boardId: string }): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const handle = useBoardDoc(boardId);
  const [objects, setObjects] = useState<CanvasObject[]>([]);
  const { viewport, zoomAt, panBy, panTo, toWorld, toScreen, setZoom, reset } = useViewport();
  const { tool, setTool } = useTool("sticky");
  const [stickyColor, setStickyColor] = useState<StickyColor>("yellow");
  const [defaultShapeKind, setDefaultShapeKind] = useState<ShapeKind>("rounded-rect");
  const [defaultShapeSemantics, setDefaultShapeSemantics] = useState<ShapeSemantics | undefined>(
    undefined,
  );
  const [defaultIconName, setDefaultIconName] = useState<string>("User");
  const { selected, select, selectMany, clear } = useSelection();
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [board, setBoard] = useState<BoardDto | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const panRef = useRef<PanState | null>(null);
  const marqueeRef = useRef<MarqueeState | null>(null);
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const autoInsertedRef = useRef<Set<string>>(new Set());
  /** Letzte Pointer-Worldposition — fuer Paste an Cursor-Stelle. */
  const lastWorldRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const yMap = useMemo<Y.Map<unknown> | null>(
    () => (handle ? (handle.doc.getMap<unknown>("objects") as Y.Map<unknown>) : null),
    [handle],
  );
  const yLinks = useMemo<Y.Array<Link> | null>(
    () => (handle ? (handle.doc.getArray<Link>("links") as Y.Array<Link>) : null),
    [handle],
  );
  const undo = useUndoManager(yMap);
  const { links, createLink, deleteLink, deleteLinksFor, updateLinkType, updateLinkRouting } =
    useLinks(yLinks, user?.id ?? null, newCanvasId);
  useTreeSync({ yMap, yLinks, userId: user?.id ?? null, newId: newCanvasId });
  const [arrowFromId, setArrowFromId] = useState<string | null>(null);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [pendingLink, setPendingLink] = useState<PendingLink | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [surfaceHeight, setSurfaceHeight] = useState<number>(0);
  const [autoRouteHint, setAutoRouteHint] = useState<string | null>(null);
  const autoRouteHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const awarenessUser = useMemo<AwarenessUser | null>(() => {
    if (!user) return null;
    return { id: user.id, name: user.displayName, color: colorForUserId(user.id) };
  }, [user]);
  const { remotePresences, setLocalCursor } = useAwareness(handle?.provider, awarenessUser);

  // Board-Metadaten laden (fuer methodTemplate auto-insert + Header-Anzeige).
  useEffect(() => {
    let cancelled = false;
    boardsApi
      .get(boardId)
      .then((b) => {
        if (!cancelled) setBoard(b);
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      });
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  // Y.Map "objects" observe.
  useEffect(() => {
    if (!yMap) return;
    const sync = (): void => {
      const next: CanvasObject[] = [];
      for (const value of yMap.values()) {
        if (isCanvasObject(value)) next.push(value);
      }
      next.sort((a, b) => a.createdAt - b.createdAt);
      setObjects(next);
    };
    sync();
    yMap.observe(sync);
    return () => yMap.unobserve(sync);
  }, [yMap]);

  // Auto-Insert beim ersten Sync: leerer Doc + Board hat methodTemplate → Scaffold einfuegen.
  useEffect(() => {
    if (!handle || !yMap || !user || !board) return;
    if (!board.methodTemplate) return;
    if (autoInsertedRef.current.has(boardId)) return;

    const tryInsert = (): void => {
      if (yMap.size > 0) {
        autoInsertedRef.current.add(boardId);
        return;
      }
      const labels = methodLabelKeys(board.methodTemplate as MethodId).map((k) => t(k));
      const rect = surfaceRef.current?.getBoundingClientRect();
      const cx = rect ? rect.width / 2 : 600;
      const cy = rect ? rect.height / 2 : 400;
      const world = toWorld(cx, cy);
      const result = insertMethodScaffold({
        methodId: board.methodTemplate as MethodId,
        yMap,
        centerX: world.x,
        centerY: world.y,
        createdBy: user.id,
        labels,
        newId: newCanvasId,
      });
      if (rect) {
        panTo(result.center.x, result.center.y, rect.width, rect.height, { duration: 0 });
      }
      autoInsertedRef.current.add(boardId);
    };

    if (handle.provider.synced) {
      tryInsert();
    } else {
      const onSync = (synced: boolean): void => {
        if (synced) tryInsert();
      };
      handle.provider.on("sync", onSync);
      return () => {
        handle.provider.off("sync", onSync);
      };
    }
  }, [handle, yMap, user, board, boardId, t, toWorld, panTo]);

  // Space → Pan.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setSpaceHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.code === "Space") setSpaceHeld(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Slash oeffnet Method-Picker.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setPickerOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Ctrl/Cmd+A selektiert alle, Escape clear.
  useEffect(() => {
    if (!yMap) return;
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === "a") {
        e.preventDefault();
        const allIds: string[] = [];
        for (const value of yMap.values()) {
          if (isCanvasObject(value)) allIds.push(value.id);
        }
        selectMany(allIds, false);
        return;
      }
      if (e.key === "Escape") {
        if (marqueeRef.current) {
          marqueeRef.current = null;
          setMarqueeRect(null);
        }
        clear();
        setSelectedLinkId(null);
        setArrowFromId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [yMap, selectMany, clear]);

  // V2 Slice 3: Clipboard (Ctrl+C/X/V), Duplicate (Ctrl+D), Z-Order (Ctrl+]/[).
  useEffect(() => {
    if (!yMap || !user) return;
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const key = e.key.toLowerCase();
      if (key === "c") {
        if (selected.size === 0) return;
        e.preventDefault();
        copySelection(yMap, yLinks, selected);
        return;
      }
      if (key === "x") {
        if (selected.size === 0) return;
        e.preventDefault();
        const ok = copySelection(yMap, yLinks, selected);
        if (!ok) return;
        yMap.doc?.transact(() => {
          for (const id of selected) {
            const raw = yMap.get(id);
            if (isCanvasObject(raw) && raw.locked) continue;
            yMap.delete(id);
          }
        });
        deleteLinksFor(selected);
        clear();
        return;
      }
      if (key === "v") {
        if (!clipboardHasContent()) return;
        e.preventDefault();
        const result = pasteClipboard(yMap, yLinks, user.id, newCanvasId);
        if (result.pastedObjectIds.length > 0) {
          selectMany(result.pastedObjectIds, false);
        }
        return;
      }
      if (key === "d") {
        if (selected.size === 0) return;
        e.preventDefault();
        const result = duplicateSelection(yMap, yLinks, selected, user.id, newCanvasId);
        if (result.pastedObjectIds.length > 0) {
          selectMany(result.pastedObjectIds, false);
        }
        return;
      }
      // Z-Order: Ctrl+] (vorne), Ctrl+[ (hinten), +Shift = ganz nach vorne/hinten.
      if (e.key === "]") {
        if (selected.size === 0) return;
        e.preventDefault();
        if (e.shiftKey) bringToFront(yMap, selected);
        else bringForward(yMap, selected);
        return;
      }
      if (e.key === "[") {
        if (selected.size === 0) return;
        e.preventDefault();
        if (e.shiftKey) sendToBack(yMap, selected);
        else sendBackward(yMap, selected);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [yMap, yLinks, user, selected, selectMany, deleteLinksFor, clear]);

  // Delete/Backspace → Selection (Objekte ODER Link) entfernen.
  useEffect(() => {
    if (!yMap) return;
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (selectedLinkId) {
        e.preventDefault();
        deleteLink(selectedLinkId);
        setSelectedLinkId(null);
        return;
      }
      if (selected.size === 0) return;
      e.preventDefault();
      // Stale-Parents vor der Loesch-Transact einsammeln: jedes mindmap-node, dessen
      // parentNodeId NICHT in der Selection liegt, ist ein moeglicher Layout-Anker fuer
      // den Subtree, der nach dem Delete neu layoutet werden muss.
      const layoutAnchors = new Set<string>();
      const newOrphanIds: string[] = [];
      for (const value of yMap.values()) {
        if (!isCanvasObject(value) || value.type !== "mindmap-node") continue;
        if (selected.has(value.id)) continue;
        if (value.parentNodeId && selected.has(value.parentNodeId)) {
          newOrphanIds.push(value.id);
        } else if (value.parentNodeId && !selected.has(value.parentNodeId)) {
          layoutAnchors.add(value.parentNodeId);
        }
      }
      yMap.doc?.transact(() => {
        // Mindmap-Orphan-Cascade: Children eines geloeschten mindmap-Node bekommen parentNodeId=null
        // damit sie als Root weiterleben. useTreeSync raeumt den dangling tree-parent-Link beim
        // naechsten Reconcile-Pass auf.
        for (const value of yMap.values()) {
          if (!isCanvasObject(value)) continue;
          if (value.type !== "mindmap-node") continue;
          if (!value.parentNodeId || !selected.has(value.parentNodeId)) continue;
          yMap.set(value.id, { ...value, parentNodeId: null });
        }
        for (const id of selected) yMap.delete(id);
        // Auto-Layout: jeden uebrig gebliebenen Subtree-Root neu anordnen + jeden Orphan
        // (der jetzt selbst Root ist) als eigener Tree layouten.
        for (const anchor of layoutAnchors) applyMindmapLayout(yMap, anchor);
        for (const orphan of newOrphanIds) applyMindmapLayout(yMap, orphan);
      });
      deleteLinksFor(selected);
      clear();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [yMap, selected, clear, deleteLinksFor, deleteLink, selectedLinkId]);

  // Pfeiltasten-Tree-Navigation fuer Mindmap-Nodes — nur wenn genau 1 mindmap-node
  // selektiert ist und nicht im Inline-Edit. Wechselt nur die Selection.
  useEffect(() => {
    if (!yMap) return;
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (selected.size !== 1) return;
      const [id] = selected;
      if (!id) return;
      const raw = yMap.get(id);
      if (!isCanvasObject(raw) || raw.type !== "mindmap-node") return;
      const key = e.key;
      if (key !== "ArrowUp" && key !== "ArrowDown" && key !== "ArrowLeft" && key !== "ArrowRight") {
        return;
      }
      const byId = new Map<string, MindmapNodeObj>();
      for (const v of yMap.values()) {
        if (isCanvasObject(v) && v.type === "mindmap-node") byId.set(v.id, v as MindmapNodeObj);
      }
      let target: string | null = null;
      if (key === "ArrowRight") target = findChild(id, byId);
      else if (key === "ArrowLeft") target = findParent(id, byId);
      else if (key === "ArrowDown") target = findSiblingNext(id, byId);
      else if (key === "ArrowUp") target = findSiblingPrev(id, byId);
      if (target) {
        e.preventDefault();
        select(target, false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [yMap, selected, select]);

  // Optional O4: Link-Auswahl + Objekt-Auswahl sind exklusiv.
  const handleSelectLink = useCallback(
    (id: string): void => {
      setSelectedLinkId((prev) => (prev === id ? null : id));
      clear();
    },
    [clear],
  );

  const handleDeleteLink = useCallback(
    (id: string): void => {
      deleteLink(id);
      setSelectedLinkId(null);
    },
    [deleteLink],
  );

  // Optional O4: Objekt-Select schliesst Link-Select.
  // V2 Slice 5: wenn Objekt eine groupId hat, Selection auf alle Group-Mitglieder ausweiten.
  const handleSelectObject = useCallback(
    (id: string, additive: boolean): void => {
      setSelectedLinkId(null);
      if (!yMap) {
        select(id, additive);
        return;
      }
      const raw = yMap.get(id);
      const groupId = isCanvasObject(raw) ? raw.groupId : undefined;
      if (!groupId) {
        select(id, additive);
        return;
      }
      const memberIds: string[] = [];
      for (const value of yMap.values()) {
        if (!isCanvasObject(value)) continue;
        if (value.groupId === groupId) memberIds.push(value.id);
      }
      selectMany(memberIds, additive);
    },
    [select, selectMany, yMap],
  );

  // Tool-Wechsel → Arrow-Source resetten + Inspector verlaesst Auswahl wenn Arrow-Modus.
  useEffect(() => {
    if (tool !== "arrow") setArrowFromId(null);
  }, [tool]);

  const onWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    zoomAt(factor, e.clientX - rect.left, e.clientY - rect.top);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Drei Pfade auf Primary-Click:
    //   - Leertaste-Hold oder Middle-Mouse: harter Pan (auch ueber Objekten).
    //   - Primary auf leere Surface + Tool=select: Marquee-Auswahl-Rechteck.
    //   - Primary auf leere Surface + Object-Tools: Pan-Tracking (Drag) oder Place (Click).
    const hardPan = spaceHeld || e.button === 1 || e.button === 2;
    // Im Placement-Modus darf der Pointer-Down auch ueber einem Objekt-/Block-/Region-/
    // Frame-DIV starten (sonst kann man nicht direkt auf eine Methode/Frame platzieren).
    const isPlacement = tool !== "select" && tool !== "arrow";
    const surfacePrimary = e.button === 0 && (e.target === e.currentTarget || isPlacement);
    if (!hardPan && !surfacePrimary) return;
    e.currentTarget.setPointerCapture(e.pointerId);

    if (hardPan) {
      // Pan NUR per Space / Middle-Mouse / Rechtsklick — normaler Linksklick pannt nie.
      e.preventDefault();
      panRef.current = {
        startScreenX: e.clientX,
        startScreenY: e.clientY,
        startTx: viewport.tx,
        startTy: viewport.ty,
        moved: false,
      };
      return;
    }

    if (tool === "select") {
      const rect = e.currentTarget.getBoundingClientRect();
      const world = toWorld(e.clientX - rect.left, e.clientY - rect.top);
      marqueeRef.current = {
        startWorld: world,
        startScreenX: e.clientX,
        startScreenY: e.clientY,
        additive: e.shiftKey,
        moved: false,
      };
      setMarqueeRect({ x: world.x, y: world.y, width: 0, height: 0 });
    }
    // Placement-Tool (Linksklick): kein Pan-Tracking — onPointerUp platziert beim Loslassen.
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const world = toWorld(e.clientX - rect.left, e.clientY - rect.top);
    setLocalCursor(world);
    lastWorldRef.current = world;

    const marquee = marqueeRef.current;
    if (marquee) {
      const dxs = e.clientX - marquee.startScreenX;
      const dys = e.clientY - marquee.startScreenY;
      if (Math.hypot(dxs, dys) > PAN_DRAG_THRESHOLD_PX) marquee.moved = true;
      setMarqueeRect(normalizeRect(marquee.startWorld, world));
      return;
    }

    const pan = panRef.current;
    if (!pan) return;
    const dx = e.clientX - pan.startScreenX;
    const dy = e.clientY - pan.startScreenY;
    if (Math.hypot(dx, dy) > PAN_DRAG_THRESHOLD_PX) pan.moved = true;
    panBy(pan.startTx + dx - viewport.tx, pan.startTy + dy - viewport.ty);
  };

  const onPointerLeave = (): void => setLocalCursor(null);

  // Bild-Upload (Paste + Drag&Drop): hochladen, dann als image-Objekt platzieren.
  // Bytes liegen im Object-Store; im CRDT steht nur die assetId.
  const handleImageFiles = useCallback(
    (files: File[], world: { x: number; y: number }): void => {
      if (!yMap || !user || files.length === 0) return;
      files.forEach((file, i) => {
        setUploadingCount((n) => n + 1);
        void uploadImage(boardId, file)
          .then(({ assetId, width, height }) => {
            const size = fitDisplaySize(width, height);
            const id = newCanvasId();
            const obj: CanvasObject = {
              id,
              type: "image",
              x: world.x + i * 24,
              y: world.y + i * 24,
              createdBy: user.id,
              createdAt: Date.now(),
              parentRegionId: null,
              assetId,
              width: size.width,
              height: size.height,
              originalName: file.name,
            };
            yMap.set(id, obj);
            select(id, false);
            setUploadError(null);
          })
          .catch((err: unknown) => {
            const code = err instanceof ImageUploadError ? err.code : "failed";
            setUploadError(t(`canvas.image.error.${code}`));
          })
          .finally(() => setUploadingCount((n) => Math.max(0, n - 1)));
      });
    },
    [yMap, user, boardId, select, t],
  );

  const onDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (Array.from(e.dataTransfer.types).includes("Files")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    const files = imageFilesFromDrop(e.dataTransfer);
    if (files.length === 0) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const world = toWorld(e.clientX - rect.left, e.clientY - rect.top);
    handleImageFiles(files, world);
  };

  // OS-Clipboard-Paste von Bildern (Screenshots). Window-Level, weil paste-Events am
  // fokussierten Element/Document feuern — nicht zuverlaessig am Surface-DIV.
  useEffect(() => {
    if (!yMap || !user) return;
    const onPaste = (e: ClipboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      const files = imageFilesFromClipboard(e.clipboardData);
      if (files.length === 0) return;
      e.preventDefault();
      handleImageFiles(files, lastWorldRef.current);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [yMap, user, handleImageFiles]);

  const placeObject = useCallback(
    (worldX: number, worldY: number, currentTool: Tool): void => {
      if (!yMap || !user) return;
      if (currentTool === "select") return;
      const id = newCanvasId();

      const base = {
        id,
        x: worldX,
        y: worldY,
        createdBy: user.id,
        createdAt: Date.now(),
        parentRegionId: null as string | null,
      };
      let obj: CanvasObject;
      switch (currentTool) {
        case "sticky":
          // Default-Groesse explizit (Plan §M1 Hybrid-Adjustment): LinkLayer/Edge-Drag-Connector
          // brauchen konsistente Model-Bounds. Auto-Grow ist out — User kann via Resize-Handle anpassen.
          obj = {
            ...base,
            type: "sticky",
            text: "Neu",
            width: 160,
            height: 96,
            color: stickyColor,
          };
          break;
        case "text":
          obj = { ...base, type: "text", text: "Text", width: 120, height: 40 };
          break;
        case "shape-rect":
          obj = { ...base, type: "shape-rect", width: 160, height: 100, label: "Form" };
          break;
        case "requirement":
          obj = {
            ...base,
            type: "requirement",
            text: t("inspector.type.requirement"),
            priority: "should",
            category: "functional",
            width: 200,
            height: 80,
          };
          break;
        case "pain":
          obj = {
            ...base,
            type: "pain",
            text: t("inspector.type.pain"),
            severity: "med",
            width: 200,
            height: 80,
          };
          break;
        case "dependency":
          obj = {
            ...base,
            type: "dependency",
            text: t("inspector.type.dependency"),
            kind: "internal",
            status: "open",
            width: 200,
            height: 90,
          };
          break;
        case "frame":
          obj = {
            ...base,
            type: "frame",
            title: t("frame.titleDefault"),
            width: 400,
            height: 280,
          };
          break;
        case "mindmap-node":
          obj = {
            ...base,
            type: "mindmap-node",
            text: "",
            parentNodeId: null,
            width: 140,
            height: 36,
          };
          break;
        case "shape": {
          // Circle quadratisch defaulten, damit er sauber rund startet.
          const isCircle = defaultShapeKind === "circle";
          obj = {
            ...base,
            type: "shape",
            kind: defaultShapeKind,
            width: isCircle ? 80 : 120,
            height: isCircle ? 80 : 60,
            text: "",
            ...(defaultShapeSemantics ? { semantics: defaultShapeSemantics } : {}),
          };
          break;
        }
        case "icon":
          obj = {
            ...base,
            type: "icon",
            iconName: defaultIconName,
            width: 48,
            height: 48,
          };
          break;
        default:
          return;
      }

      // Generic Region-Routing: wenn unter Pointer eine akzeptierende Region liegt,
      // rastet das Objekt in einen freien Slot ein, wird Region-Member (parentRegionId)
      // und uebernimmt ALLE Hint-Keys (priority/category/severity/...). Hint-lose
      // Regionen (Lanes, Phasen, Context) liefern einen leeren Patch — nur Membership.
      const region = findRegionAt(yMap, worldX, worldY);
      if (region && regionAcceptsType(region, currentTool)) {
        yMap.doc?.transact(() => {
          const result = autoRouteIntoRegion(yMap, obj, region);
          if (result) {
            obj = {
              ...obj,
              x: result.x,
              y: result.y,
              parentRegionId: region.id,
              ...result.hintPatch,
            } as CanvasObject;
          }
        });
      } else {
        // Kein Region-Treffer: faellt das Objekt auf einen Frame, wird es Frame-Member
        // (parentFrameId) und bleibt an der Drop-Position (frei) — bewegt sich mit dem Frame.
        const frame = findFrameAt(yMap, worldX, worldY);
        if (frame) {
          const pos = autoRouteIntoFrame(frame, obj);
          obj = { ...obj, x: pos.x, y: pos.y, parentFrameId: frame.id } as CanvasObject;
        }
      }

      yMap.set(id, obj);
      // UX: nach Drop zurueck auf "Auswahl" + neues Objekt selektieren — der User soll
      // nicht versehentlich weitere Elemente platzieren. Miro-Verhalten.
      setTool("select");
      select(id, false);
    },
    [
      yMap,
      user,
      t,
      stickyColor,
      defaultShapeKind,
      defaultShapeSemantics,
      defaultIconName,
      setTool,
      select,
    ],
  );

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Marquee-Path: wenn Drag, Selection per Hit-Test. Wenn nicht moved, Clear-Click.
    const marquee = marqueeRef.current;
    if (marquee) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {}
      const rectEnd = e.currentTarget.getBoundingClientRect();
      const endWorld = toWorld(e.clientX - rectEnd.left, e.clientY - rectEnd.top);
      const finalRect = normalizeRect(marquee.startWorld, endWorld);
      const wasDrag = marquee.moved;
      marqueeRef.current = null;
      setMarqueeRect(null);
      if (wasDrag) {
        const ids = pickObjectsInRect(finalRect, objects);
        selectMany(ids, marquee.additive);
        return;
      }
      // Kein Drag → wie alter Surface-Klick: Selection clearen.
      if (!marquee.additive) clear();
      return;
    }

    if (panRef.current) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      const wasPan = panRef.current.moved;
      panRef.current = null;
      if (wasPan) return;
    }
    // Placement-Pfad: Pointer-Capture vom Down freigeben (kein panRef/marquee aktiv).
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    if (e.button !== 0) return;
    if (spaceHeld) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const world = toWorld(e.clientX - rect.left, e.clientY - rect.top);

    if (tool === "arrow") {
      // Nur bei Klick auf die leere Surface die Quelle abbrechen. Pointer-Up, der von
      // einem Element hochbubbelt, darf arrowFromId NICHT zuruecksetzen — sonst wird der
      // Ziel-Klick (onArrowPick) entwertet und es kommt nie eine Verbindung zustande.
      if (e.target === e.currentTarget) setArrowFromId(null);
      return;
    }
    if (tool === "select") {
      // Clear nur bei Klick auf die leere Surface — Klick auf ein Objekt selektiert es.
      if (e.target === e.currentTarget) clear();
      return;
    }
    // Placement feuert auch ueber Objekt-/Methoden-/Frame-DIVs (Welt-Koordinaten-basiert).
    placeObject(world.x, world.y, tool);
  };

  const onPortDown = useCallback(
    (fromId: string, edge: PortEdge, e: React.PointerEvent<HTMLDivElement>): void => {
      if (!yMap) return;
      e.stopPropagation();
      e.preventDefault();
      const raw = yMap.get(fromId);
      if (!isCanvasObject(raw)) return;
      const fromPoint = portWorldPoint(raw, edge);
      const surface = surfaceRef.current;
      if (!surface) return;
      const rect0 = surface.getBoundingClientRect();
      const start = toWorld(e.clientX - rect0.left, e.clientY - rect0.top);
      setPendingLink({ fromPoint, currentPoint: start });

      const onMove = (mv: PointerEvent): void => {
        const surf = surfaceRef.current;
        if (!surf) return;
        const r = surf.getBoundingClientRect();
        const w = toWorld(mv.clientX - r.left, mv.clientY - r.top);
        setPendingLink((prev) => (prev ? { ...prev, currentPoint: w } : null));
      };
      const onUp = (up: PointerEvent): void => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        setPendingLink(null);
        const el = document.elementFromPoint(up.clientX, up.clientY);
        const objEl = el instanceof Element ? el.closest("[data-id]") : null;
        const targetId = objEl?.getAttribute("data-id") ?? null;
        if (targetId && targetId !== fromId) {
          createLink(fromId, targetId);
        }
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [yMap, toWorld, createLink],
  );

  const onArrowPick = useCallback(
    (id: string): void => {
      if (arrowFromId === null) {
        setArrowFromId(id);
        return;
      }
      if (arrowFromId === id) {
        setArrowFromId(null);
        return;
      }
      createLink(arrowFromId, id);
      setArrowFromId(null);
    },
    [arrowFromId, createLink],
  );

  const objectsById = useMemo<ReadonlyMap<string, CanvasObject>>(() => {
    const map = new Map<string, CanvasObject>();
    for (const obj of objects) map.set(obj.id, obj);
    return map;
  }, [objects]);

  // Selection-Bounding-Box (World-Coords). Multi: Union ueber alle Selektionen.
  // Single: einzelnes Objekt — wird auch von der FloatingPropertyBar als Anchor genutzt.
  const selectionBounds = useMemo<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(() => {
    if (selected.size < 1) return null;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const id of selected) {
      const obj = objectsById.get(id);
      if (!obj) continue;
      const b = objectBounds(obj);
      minX = Math.min(minX, obj.x);
      minY = Math.min(minY, obj.y);
      maxX = Math.max(maxX, obj.x + b.w);
      maxY = Math.max(maxY, obj.y + b.h);
    }
    if (!Number.isFinite(minX)) return null;
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }, [selected, objectsById]);

  // Anker fuer die Link-Eigenschaftsleiste: Mittelpunkt zwischen den beiden verbundenen
  // Objekten (Null-Groesse-Rect). Objekt- und Link-Auswahl schliessen sich gegenseitig aus
  // (handleSelectLink/handleSelectObject), daher kollidiert das nicht mit selectionBounds.
  const linkAnchorBounds = useMemo<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(() => {
    if (!selectedLinkId) return null;
    const link = links.find((l) => l.id === selectedLinkId);
    if (!link) return null;
    const from = objectsById.get(link.from);
    const to = objectsById.get(link.to);
    if (!from || !to) return null;
    const fb = objectBounds(from);
    const tb = objectBounds(to);
    const cx = (from.x + fb.w / 2 + (to.x + tb.w / 2)) / 2;
    const cy = (from.y + fb.h / 2 + (to.y + tb.h / 2)) / 2;
    return { x: cx, y: cy, width: 0, height: 0 };
  }, [selectedLinkId, links, objectsById]);

  // Surface-Hoehe tracken — die FloatingPropertyBar braucht sie fuer Anchor-Flip oben/unten.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    setSurfaceHeight(el.clientHeight);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setSurfaceHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const objectLayerMode: ObjectLayerMode =
    tool === "select" ? "select" : tool === "arrow" ? "arrow" : "none";

  const handlePickMethod = useCallback(
    (methodId: MethodId): void => {
      setPickerOpen(false);
      if (!yMap || !user) return;
      const labels = methodLabelKeys(methodId).map((k) => t(k));
      const rect = surfaceRef.current?.getBoundingClientRect();
      const cx = rect ? rect.width / 2 : 600;
      const cy = rect ? rect.height / 2 : 400;
      const world = toWorld(cx, cy);
      const result = insertMethodScaffold({
        methodId,
        yMap,
        centerX: world.x,
        centerY: world.y,
        createdBy: user.id,
        labels,
        newId: newCanvasId,
      });
      if (rect) {
        panTo(result.center.x, result.center.y, rect.width, rect.height);
      }
      if (result.autoRoutedCount > 0) {
        if (autoRouteHintTimerRef.current) clearTimeout(autoRouteHintTimerRef.current);
        setAutoRouteHint(t("canvas.moscow.autoRouted", { count: result.autoRoutedCount }));
        autoRouteHintTimerRef.current = setTimeout(() => setAutoRouteHint(null), 5000);
      }
      // Tool nach Insert auf "select" zuruecksetzen — analog placeObject-Pfad. Sonst
      // bleibt das vorher aktive Tool (z.B. "pain") aktiv und der naechste Klick legt
      // versehentlich Pain-Karten ab.
      setTool("select");
    },
    [yMap, user, t, toWorld, panTo, setTool],
  );

  // Auto-Route-Hint Timer beim Unmount aufraeumen.
  useEffect(() => {
    return () => {
      if (autoRouteHintTimerRef.current) clearTimeout(autoRouteHintTimerRef.current);
    };
  }, []);

  const canRename = !!board && board.status === "active" && board.ownerId === user?.id;

  const startRename = (): void => {
    if (!board || !canRename) return;
    setNameDraft(board.name);
    setEditingName(true);
  };

  const commitRename = async (): Promise<void> => {
    if (!board) return;
    const next = normalizeRename(nameDraft, board.name);
    if (next === null) {
      setEditingName(false);
      return;
    }
    setRenameBusy(true);
    try {
      const updated = await boardsApi.rename(board.id, next);
      setBoard(updated);
    } catch {
      // Rename fehlgeschlagen (403/Netz) → Draft verwerfen, Titel bleibt unveraendert.
    } finally {
      setRenameBusy(false);
      setEditingName(false);
    }
  };

  const statusLabel = handle?.status ?? "—";
  const transform = `translate(${viewport.tx}px, ${viewport.ty}px) scale(${viewport.zoom})`;
  const gridSize = GRID_BASE_PX * viewport.zoom;
  const gridBg: React.CSSProperties = {
    backgroundSize: `${gridSize}px ${gridSize}px, ${gridSize}px ${gridSize}px`,
    backgroundPosition: `${viewport.tx}px ${viewport.ty}px, ${viewport.tx}px ${viewport.ty}px`,
  };

  return (
    <div className="canvas-root" data-status={statusLabel}>
      <div className="canvas-topbar">
        <span className="canvas-tool-pill" data-tool={tool}>
          {t(`canvas.tool.${tool}`)}
        </span>
        {board &&
          (editingName ? (
            <input
              className="canvas-board-name-input"
              value={nameDraft}
              disabled={renameBusy}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => void commitRename()}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commitRename();
                if (e.key === "Escape") setEditingName(false);
              }}
              // biome-ignore lint/a11y/noAutofocus: Inline-Rename oeffnet on demand, Fokus erwuenscht
              autoFocus
            />
          ) : (
            <span
              className="canvas-board-name"
              style={canRename ? { cursor: "text" } : undefined}
              title={canRename ? t("canvas.boardName.renameHint") : undefined}
              onDoubleClick={canRename ? startRename : undefined}
            >
              {board.name}
            </span>
          ))}
        <span className="canvas-hint">
          {t("canvas.hint")} {t("canvas.edit.hint")}
        </span>
        <button
          type="button"
          className="canvas-method-trigger"
          onClick={() => setPickerOpen(true)}
          title={t("methodPicker.shortcut")}
        >
          + {t("methodPicker.cta")}
        </button>
        <button
          type="button"
          className={`canvas-snap-trigger ${snapEnabled ? "is-active" : ""}`}
          onClick={() => setSnapEnabled((p) => !p)}
          aria-pressed={snapEnabled}
          title={t("canvas.snap.tooltip")}
        >
          {t("canvas.snap.label")}
        </button>
        <button type="button" className="canvas-export-trigger" onClick={() => setExportOpen(true)}>
          {t("export.button")}
        </button>
        <button type="button" className="canvas-share-trigger" onClick={() => setShareOpen(true)}>
          {t("share.button")}
        </button>
        <SettingsMenu />
        <Presence self={awarenessUser} presences={remotePresences} />
        {autoRouteHint && (
          <output className="canvas-auto-route-hint" aria-live="polite">
            {autoRouteHint}
          </output>
        )}
        {uploadingCount > 0 && (
          <output className="canvas-upload-status" aria-live="polite">
            {t("canvas.image.uploading")}
          </output>
        )}
        {uploadError && (
          <output className="canvas-upload-error" role="alert">
            {uploadError}
          </output>
        )}
      </div>
      <div className="canvas-stage">
        <ToolRail
          tool={tool}
          setTool={setTool}
          stickyColor={stickyColor}
          setStickyColor={setStickyColor}
          shapeKind={defaultShapeKind}
          setShapeKind={setDefaultShapeKind}
          shapeSemantics={defaultShapeSemantics}
          setShapeSemantics={setDefaultShapeSemantics}
          iconName={defaultIconName}
          setIconName={setDefaultIconName}
        />
        <div
          ref={surfaceRef}
          className={`canvas-surface ${spaceHeld ? "is-panning" : ""} canvas-tool-${tool}`}
          style={gridBg}
          onWheel={onWheel}
          onContextMenu={(e) => e.preventDefault()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          role="presentation"
        >
          <div className="canvas-world" style={{ transform, transformOrigin: "0 0" }}>
            {yMap && (
              <BoardIdContext.Provider value={boardId}>
                <ObjectLayer
                  objects={objects}
                  selection={selected}
                  zoom={viewport.zoom}
                  yMap={yMap}
                  onSelect={handleSelectObject}
                  onArrowPick={onArrowPick}
                  mode={objectLayerMode}
                  arrowFromId={arrowFromId}
                  onPortDown={onPortDown}
                  snapEnabled={snapEnabled}
                  onEditingChange={setEditingId}
                />
              </BoardIdContext.Provider>
            )}
            <LinkLayer
              links={links}
              objectsById={objectsById}
              zoom={viewport.zoom}
              onSelectLink={handleSelectLink}
              selectedLinkId={selectedLinkId}
              pendingLink={pendingLink}
            />
            <RemoteCursors presences={remotePresences} zoom={viewport.zoom} />
            {marqueeRect && (
              <div
                className="canvas-marquee"
                style={{
                  left: marqueeRect.x,
                  top: marqueeRect.y,
                  width: marqueeRect.width,
                  height: marqueeRect.height,
                }}
              />
            )}
          </div>
          {yMap && (
            <FloatingPropertyBar
              selectedIds={selected}
              yMap={yMap}
              anchorBounds={selectedLinkId ? linkAnchorBounds : selectionBounds}
              toScreen={toScreen}
              zoom={viewport.zoom}
              surfaceHeight={surfaceHeight}
              editingId={editingId}
              selectedLink={
                selectedLinkId ? (links.find((l) => l.id === selectedLinkId) ?? null) : null
              }
              onUpdateLinkType={updateLinkType}
              onUpdateLinkRouting={updateLinkRouting}
              onDeleteLink={handleDeleteLink}
            />
          )}
        </div>
      </div>
      <BottomStrip
        status={statusLabel}
        objectCount={objects.length}
        selectedCount={selected.size}
        zoom={viewport.zoom}
        setZoom={setZoom}
        resetView={reset}
        undo={undo}
      />
      <MethodPickerModal
        open={pickerOpen}
        onPick={handlePickMethod}
        onClose={() => setPickerOpen(false)}
      />
      <ExportModal
        open={exportOpen}
        boardId={boardId}
        boardName={board?.name ?? null}
        onClose={() => setExportOpen(false)}
      />
      {board && (
        <ShareModal
          open={shareOpen}
          boardId={boardId}
          ownerId={board.ownerId}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}
